import type { LineupMarketRows } from "dds/organisms/channel-lineup/lineup-market.shared";
import { SERVICE_LOCALS_TIMEOUT_MS, type RawStation, type Station } from "dds/services/zip/local-rsn-channels";
import { toLineupRsns } from "lib/channel-lineup/lineup-market";
import type { LineupPlatform } from "lib/channel-lineup/lineup-model";
import {
  localChannelsFrom,
  rsnGenreCodes,
  withoutMyHomeTeamDuplicates,
  type LocalStationRules
} from "lib/channel-lineup/local-stations";
import { marketRowsFor } from "lib/channel-lineup/market-rows";

/**
 * The market's local stations from the existing `/api/local-channels`, or `null` for no usable
 * answer: a DMA that is not three digits, a non-ok status, `{ enabled: false }`, no list, an error,
 * the timeout or the caller's abort. `null` and `[]` both draw no locals: beam is never a locals
 * fallback (design-conflicts/channel-lineup.mdx, "Locals by billing code").
 */
export async function fetchLineupLocals(
  nielsenDma: string,
  platform: LineupPlatform,
  basePath = "",
  signal?: AbortSignal
): Promise<RawStation[] | null> {
  if (!/^\d{3}$/.test(nielsenDma ?? "")) {
    return null;
  }
  const controller = typeof AbortController === "undefined" ? undefined : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), SERVICE_LOCALS_TIMEOUT_MS) : undefined;
  const onAbort = () => controller?.abort();
  signal?.addEventListener?.("abort", onAbort);
  try {
    const response = await fetch(
      `${basePath}/api/local-channels?dma=${encodeURIComponent(nielsenDma)}&platform=${platform}`,
      { signal: controller?.signal }
    );
    if (!response?.ok) {
      return null;
    }
    const body = await response.json();
    return Array.isArray(body?.localChannels) ? body.localChannels : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener?.("abort", onAbort);
  }
}

export interface LineupMarketInput {
  /** The market's stations from `/api/local-channels`; `null` = not asked yet or no answer, drawn as no locals. */
  stations: readonly RawStation[] | null;
  /** beam's in-market RSNs, from the ZIP session. */
  rsnChannels: readonly Station[] | null | undefined;
  rules: LocalStationRules | undefined;
  columnCodes: readonly string[];
}

/** Locals and RSNs for the visitor's market, shaped as this lineup's rows. */
export function lineupMarketFor({ stations, rsnChannels, rules, columnCodes }: LineupMarketInput): LineupMarketRows {
  // Genre codes go on before the MyHome Team dedupe, so the RSN that replaces a local is in the pack.
  const rsns = toLineupRsns(rsnChannels ? [...rsnChannels] : undefined).map(rsn => {
    const codes = rsnGenreCodes(rsn, rules);
    return codes.length === 0 ? rsn : {
      ...rsn,
      packages: {
        ...rsn.packages,
        ...Object.fromEntries(codes.map(code => [code, "included" as const]))
      }
    };
  });
  const rows = marketRowsFor(localChannelsFrom(stations, rules), rsns, columnCodes);
  const localChannels = withoutMyHomeTeamDuplicates(rows.localChannels, rows.rsns, rules);
  const kept = new Set(localChannels.map(channel => channel.id));
  return {
    ...rows,
    localChannels,
    packChannels: rows.packChannels.filter(channel => channel.kind !== "local" || kept.has(channel.id))
  };
}
